import React, { useState, useEffect, useCallback } from "react";
import { Auth } from "aws-amplify";
import { jwtDecode } from "jwt-decode";
import { useLoading } from "../../../../context/LoadingContext";
import { useNavigate } from "react-router-dom";
import DataTable from "../../../Requirement/components/common/ReusableTable";
import SearchFilter from "../../../Requirement/components/common/SearchFilter";
import {
  fetchData,
  searchfetchQueries,
} from "../../../Requirement/services/RequirementServices";
import OfferDetails from "../../requirement/component/OfferDetails";
import RFQForm from "./components/RFQForm";
import { Box, Tabs, Tab } from "@mui/material";
import AlertModal from "../../../Requirement/components/common/AlerModel";
import {
  openAgreementPopup,
  extractOfferDataForAgreement,
} from "../../../../services/AgreementTemplate";
import TrustScoreModal from "../../../../components/common/TrustScoreModal";

const AcceptedOffers = ({ requirementDetails }) => {
  const navigate = useNavigate();
  const { setLoading, loading } = useLoading();
  const [activeUser, setActiveUser] = useState("");
  const [offerList, setOfferList] = useState([]);
  const [selectedRFQ, setSelectedRFQ] = useState(null);
  const [referenceOffers, setReferenceOffers] = useState([]);
  const [rowStart, setRowStart] = useState(0);
  const [pageSize] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [error, setError] = useState(null);
  const [INRToUsd] = useState(0.012);
  const [searchValues, setSearchValues] = useState({
    rfqNumber: "",
    deliveryDate: "",
    rfqDate: "",
    status: "",
  });

  const [productList, setProductList] = useState([]);
  const [allProductMaster, setAllProductMaster] = useState([]);

  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    autoClose: false,
    isConfirm: false,
    onConfirm: null,
  });

  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  const [tabValue, setTabValue] = useState(0);
  const TabPanel = ({ children, value, index }) => {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`tabpanel-${index}`}
        aria-labelledby={`tab-${index}`}
      >
        {value === index && <Box>{children}</Box>}
      </div>
    );
  };

  const handleTabChange = useCallback((event, newValue) => {
    setTabValue(newValue);
  }, []);

  // State for Trust Score Modal
  const [trustModal, setTrustModal] = useState(false);

  const trustModel = useCallback(() => {
    setTrustModal(true);
  }, []);

  // Helper function to determine which tab should show the download button
  const shouldShowDownloadButton = useCallback(
    (tabIndex, selectedOffer, referenceOffers) => {
      console.log(
        "Checking download button for tab:",
        tabIndex,
        "selectedOffer ReferenceOfferID:",
        selectedOffer?.ReferenceOfferID
      );

      // If selectedOffer has ReferenceOfferID, find the matching reference offer tab
      if (selectedOffer?.ReferenceOfferID) {
        const referenceIndex = referenceOffers.findIndex(
          (offer) => offer.GPOfferID === selectedOffer.ReferenceOfferID
        );
        console.log(
          "Reference index found:",
          referenceIndex,
          "for ReferenceOfferID:",
          selectedOffer.ReferenceOfferID
        );

        // If found, show button on that reference tab (index + 1 because tab 0 is the main offer)
        if (referenceIndex !== -1) {
          const shouldShow = tabIndex === referenceIndex + 1;
          console.log(
            `Tab ${tabIndex} should show download button:`,
            shouldShow,
            "(reference tab:",
            referenceIndex + 1,
            ")"
          );
          return shouldShow;
        }
      }
      // Default to main offer tab (index 0)
      const shouldShow = tabIndex === 0;
      console.log(
        `Tab ${tabIndex} should show download button:`,
        shouldShow,
        "(main offer tab)"
      );
      return shouldShow;
    },
    []
  );

  // Helper function to determine which tab should be selected
  const determineSelectedTab = useCallback((selectedOffer, referenceOffers) => {
    console.log("Determining tab selection:", {
      selectedOffer: selectedOffer,
      referenceOfferID: selectedOffer?.ReferenceOfferID,
      referenceOffers: referenceOffers,
    });

    // If selectedOffer has ReferenceOfferID, find the matching reference offer tab
    if (selectedOffer?.ReferenceOfferID) {
      const referenceIndex = referenceOffers.findIndex(
        (offer) => offer.GPOfferID === selectedOffer.ReferenceOfferID
      );
      console.log("Reference index found:", referenceIndex);

      // If found, select that tab (index + 1 because tab 0 is the main offer)
      if (referenceIndex !== -1) {
        console.log("Selecting reference tab:", referenceIndex + 1);
        return referenceIndex + 1;
      }
    }
    // Default to main offer tab (index 0)
    console.log("Selecting main offer tab: 0");
    return 0;
  }, []);

  const AcceptedOffers = useCallback(
    async (newRowStart = rowStart) => {
      setLoading(true);
      setError(null);
      try {
        const body = {
          target: "listBuyerExtendedRequirementsItems",
          rowStart: newRowStart,
          pageSize: pageSize,
          filterTerms: {
            CategoryType: "rfq",
            OfferStatusExtended: "Accepted",
            GPEmailID: activeUser,
            Deleted: 0, // Only fetch non-deleted items
          },
        };

        const response = await fetchData(body);
        if (Array.isArray(response.items) || Array.isArray(response)) {
          const updatedOffers = response.items.map((item) => ({
            ...item,
            id: item.BuyerItemExtendedID,
          }));
          setOfferList(updatedOffers);
          setTotalItems(response.total || 0);
        } else {
          throw new Error("Unexpected response format");
        }
      } catch (error) {
        console.error("Error loading matching offers:", error);
        setError("Failed to load matching offers. Please try again.");
        setOfferList([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [rowStart, pageSize, setLoading, activeUser]
  );

  const getRequirementDetails = useCallback(async (requirementsID) => {
    try {
      const response = await fetchData({
        target: "listRequirements",
        userOrder: "RequirementsID",
        rowStart: 0,
        pageSize: 1,
        filterTerms: { RequirementsID: requirementsID },
      });

      if (response.items && response.items.length > 0) {
        const rfq = response.items[0];
        console.log("=============RFQ==================", rfq);

        setSelectedRFQ(rfq);
      }
    } catch (error) {
      console.error("Error fetching requirement details:", error);
      setSelectedRFQ(null);
    }
  }, []);

  const getReferenceOffers = useCallback(async (rowSelected) => {
    setReferenceOffers([]); // Clear previous offers
    try {
      console.log("Fetching reference offers...", rowSelected);
      const body = {
        target: "listBuyerItemRFQDetails",
        userOrder: "CreatedAt ASC",
        rowStart: 0,
        pageSize: 50,
        filterTerms: {
          orGPOOfferStatus: ["Firmoffer", "BAFO"],
          ReferenceOffer: rowSelected.GPOfferID,
          RequirementsID: rowSelected.RequirementsID,
        },
      };

      const response = await fetchData(body);
      console.log(response.items);

      if (response.items && response.items.length > 0) {
        // Deduplicate based on GPOfferID to avoid showing same offer multiple times
        const uniqueOffers = response.items.reduce((acc, current) => {
          const existingOffer = acc.find(
            (offer) => offer.GPOfferID === current.GPOfferID
          );
          if (!existingOffer) {
            acc.push(current);
          }
          return acc;
        }, []);

        console.log("Unique offers:", uniqueOffers);
        setReferenceOffers(uniqueOffers);
        return uniqueOffers; // Return the loaded offers
      }
      return []; // Return empty array if no offers found
    } catch (error) {
      console.error("Error fetching requirement details:", error);
      setReferenceOffers([]);
      return []; // Return empty array on error
    }
  }, []);

  const fields = [
    {
      name: "rfqNumber",
      label: "RFQ Number",
      type: "text",
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, rfqNumber: value })),
      value: searchValues.rfqNumber,
    },
    {
      name: "deliveryDate",
      label: "Delivery Date",
      type: "date",
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, deliveryDate: value })),
      value: searchValues.deliveryDate,
    },
    {
      name: "rfqDate",
      label: "RFQ Date",
      type: "date",
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, rfqDate: value })),
      value: searchValues.rfqDate,
    },
  ];

  const handlePageChange = useCallback(
    (page) => {
      const newRowStart = (page - 1) * pageSize;
      setRowStart(newRowStart);
    },
    [pageSize]
  );

  const currentPage = Math.floor(rowStart / pageSize) + 1;
  const formatPrice = (value) => {
    if (!value || value === "") return "0.00";
    const numValue = parseFloat(value);
    return isNaN(numValue) ? "0.00" : numValue.toFixed(2);
  };

  const convertToUSD = (inrValue) => {
    if (!inrValue || inrValue === "") return "0.00";
    const usdValue = parseFloat(inrValue) * INRToUsd;
    return isNaN(usdValue) ? "0.00" : usdValue.toFixed(2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const columns = [
    { id: "RequirementNumber", numeric: false, label: "RFQ Number" },
    { id: "BuyerEmailID", numeric: false, label: "Buyer Email ID" },
    { id: "ProductName", numeric: false, label: "Product" },
    { id: "Grade", numeric: false, label: "Grade" },
    {
      id: "Quantity",
      numeric: true,
      label: "Quantity (MT)",
      renderCell: (row) => formatPrice(row.Quantity),
    },
    {
      id: "CIFSalePrice",
      numeric: true,
      label: "CIF Sale Price",
      renderCell: (row) => convertToUSD(row.CIFSalePrice),
    },
    { id: "Country", numeric: false, label: "Origin Country" },
    { id: "DestinationCountry", numeric: false, label: "Destination" },
    { id: "INCO", numeric: false, label: "INCO Terms" },
    {
      id: "GPOOfferStatus",
      numeric: false,
      label: "Offer Status",
      renderCell: (row) => {
        let bgColor = "";
        let textColor = "";

        switch (row.GPOOfferStatus) {
          case "BAFO":
            bgColor = "#e0f2fe";
            textColor = "#0369a1";
            break;
          case "Firmoffer":
            bgColor = "#d1fae5";
            textColor = "#065f46";
            break;
          default:
            bgColor = "#fef3c7";
            textColor = "#b45309";
            break;
        }

        return (
          <span
            style={{
              backgroundColor: bgColor,
              color: textColor,
              padding: "4px 10px",
              borderRadius: "999px",
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            {row.GPOOfferStatus}
          </span>
        );
      },
    },
    {
      id: "CreatedAt",
      numeric: false,
      label: "Created Date",
      renderCell: (row) => formatDate(row.CreatedAt),
    },
    {
      id: "Expires",
      numeric: false,
      label: "Expires",
      renderCell: (row) => formatDate(row.Expires),
    },
  ];

  const getBuyerInfo = useCallback(async (buyerEmail) => {
    try {
      const response = await fetchData({
        target: "listAMTPLUsers",
        userOrder: "AMTPLUserID",
        rowStart: 0,
        pageSize: 100,
        filterTerms: { EmailID: buyerEmail },
      });
      // Buyer info loaded but not stored in state as it's not used
      console.log("Buyer info loaded:", response.items || []);
    } catch (error) {
      console.error("Error loading buyer info:", error);
    }
  }, []);

  const loadAllProducts = useCallback(async () => {
    setLoading(true);
    try {
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listProducts",
          userOrder: "ProductID",
          rowStart: 0,
          pageSize: 1,
        }),
      ]);
      const totalCount = initialResponse?.total || initialResponse?.length || 0;
      if (totalCount > 0) {
        const [allProducts] = await Promise.all([
          searchfetchQueries({
            target: "listProducts",
            userOrder: "ProductID",
            rowStart: 0,
            pageSize: totalCount,
          }),
        ]);
        setAllProductMaster(allProducts);
      } else {
        setAllProductMaster([]);
      }
    } catch (error) {
      console.error("Error loading products: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadProductList = useCallback(async () => {
    setLoading(true);
    try {
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listProducts",
          userOrder: "ProductName",
          rowStart: 0,
          pageSize: 1,
          filterTerms: { Status: "ACTIVE" },
        }),
      ]);

      const totalCount = initialResponse?.total || initialResponse?.length || 0;

      if (totalCount > 0) {
        const [allProducts] = await Promise.all([
          searchfetchQueries({
            target: "listProducts",
            userOrder: "ProductName",
            rowStart: 0,
            pageSize: totalCount,
            filterTerms: { Status: "ACTIVE" },
          }),
        ]);

        const distinctProducts = Array.from(
          new Map(allProducts.map((p) => [p.ProductName, p])).values()
        );
        setProductList(distinctProducts);
      } else {
        setProductList([]);
      }
    } catch (error) {
      console.error("Error loading products: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);
        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_GLOBAL_PARTNERS")) {
          await Auth.signOut();
          navigate("/login/global-partner");
        } else {
          setActiveUser(user.attributes.email);
          getBuyerInfo(user.attributes.email);
        }
      } catch (error) {
        console.error("Error checking user:", error);
        await Auth.signOut();
        navigate("/login/global-partner");
      }
    };
    checkUser();
  }, [navigate, getBuyerInfo]);

  useEffect(() => {
    if (activeUser) {
      getBuyerInfo(activeUser);
      loadProductList();
      loadAllProducts();
      AcceptedOffers();
    }
  }, [
    activeUser,
    getBuyerInfo,
    loadProductList,
    loadAllProducts,
    AcceptedOffers,
  ]);

  const handleRowClick = useCallback(
    async (row) => {
      setSelectedOffer(row);
      console.log("============= clicked row", row);
      getRequirementDetails(row.RequirementsID);
      const loadedReferenceOffers = await getReferenceOffers(row);
      console.log(
        "??????????????????????????????? loadedReferenceOffers",
        loadedReferenceOffers,
        row,
        row.ReferenceOfferID
      );

      // Determine which tab should be selected based on ReferenceOfferID
      const selectedTabIndex = determineSelectedTab(row, loadedReferenceOffers);
      setTabValue(selectedTabIndex);
    },
    [getRequirementDetails, getReferenceOffers, determineSelectedTab]
  );

  const handleSearch = () => {
    setRowStart(0);
    AcceptedOffers(0);
  };

  const handleAgreementPopup = useCallback(
    async (referenceOffer) => {
      const offerToUse = referenceOffer || selectedOffer;

      if (!offerToUse) {
        console.error("No offer selected for agreement generation");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Fetch user data using the specified payload
        const body = {
          target: "getUserInfoByEmail",
          EmailID: referenceOffer?.OfferFor || selectedOffer?.BuyerEmailID,
        };

        console.log("Fetching user info for agreement:", body);
        const userDataResponse = await fetchData(body);

        console.log("User data response:", userDataResponse);

        // Extract user information from the response
        const userData = userDataResponse?.data || {};

        // Extract and prepare offer data for the agreement
        const agreementData = extractOfferDataForAgreement({
          ...offerToUse,
          // Add GP information from fetched user data
          buyerName:
            userData.FullName ||
            `${userData.FirstName || ""} ${userData.LastName || ""}`.trim() ||
            "",
          buyerAddress: userData.Address1 || userData.CompanyAddress1 || "",
          buyerContact:
            `${userData.Phone1}, ${userData.EmailID}` || userData.ContactNumber,
        });

        console.log("Agreement data prepared:", agreementData);

        // Open the agreement popup
        openAgreementPopup(agreementData);
      } catch (error) {
        console.error("Error opening agreement popup:", error);
        setModal({
          show: true,
          type: "error",
          title: "Error",
          message: "Failed to open agreement. Please try again.",
          autoClose: false,
          isConfirm: false,
          onConfirm: null,
        });
      } finally {
        setLoading(false);
      }
    },
    [selectedOffer, setLoading]
  );

  const Button = ({ label, onClick }) => {
    return (
      <button
        className="btn-action-re"
        onClick={onClick}
        style={{ width: "140px" }}
      >
        {label}
      </button>
    );
  };

  // Pass the selectedOffer ID to DataTable via selectedRows
  const selectedRows = selectedOffer ? [selectedOffer.BuyerItemExtendedID] : [];

  return (
    <div className="matching-offers-container">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        onCancel={handleModalClose}
        buttonText="OK"
        isConfirm={modal.isConfirm}
        onConfirm={modal.onConfirm}
        autoClose={modal.autoClose}
      />
      <div className="table-header">
        <div className="header-text">
          <div className="head-text">Accepted Offers</div>
        </div>
        <div className="head-btn"></div>
      </div>
      <div className="search-filter row">
        <SearchFilter fields={fields} onSearch={handleSearch} />
      </div>
      {loading && <div className="loading">Loading offers...</div>}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && offerList.length === 0 && (
        <div className="empty-state">No matching offers found.</div>
      )}
      {!loading && offerList.length > 0 && (
        <DataTable
          columns={columns}
          data={offerList}
          total={totalItems}
          title="Matching Offers Information"
          rowsPerPage={pageSize}
          onPageChange={handlePageChange}
          currentPage={currentPage}
          onRowClick={handleRowClick}
          selectedRows={selectedRows} // Pass selected row ID
          rowIdField="BuyerItemExtendedID" // Use unique identifier for rows
        />
      )}
      {selectedOffer && (
        <>
          {/* Offer Details Section */}
          <div className="tab-content">
            <Box sx={{ borderBottom: 1, borderColor: "#de6b14" }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="Offer info tabs"
                sx={{
                  "& .MuiTab-root": {
                    color: "#000",
                    backgroundColor: "transparent",
                    "&.Mui-selected": {
                      color: "#fff",
                      backgroundColor: "#de6b14",
                      borderBottom: "2px solid #d4d4d4",
                    },
                  },
                  "& .MuiTabs-indicator": { backgroundColor: "#de6b14" },
                }}
              >
                <Tab
                  label={
                    <div style={{ textAlign: "center" }}>
                      <div>
                        {selectedOffer.OfferNumber}{" "}
                        {selectedOffer.CreatedAt
                          ? `(${formatDate(selectedOffer.CreatedAt)})`
                          : ""}
                      </div>

                      <div style={{ fontSize: "8px", opacity: 0.7 }}>
                        Initial offer
                      </div>
                    </div>
                  }
                  id="tab-0"
                  aria-controls="tabpanel-0"
                />
                {referenceOffers.map((offer, index) => (
                  <Tab
                    key={index}
                    label={
                      <div style={{ textAlign: "center" }}>
                        <div>
                          {referenceOffers[index].OfferNumber}{" "}
                          {referenceOffers[index].CreatedAt
                            ? `(${formatDate(
                                referenceOffers[index].CreatedAt
                              )})`
                            : ""}
                        </div>
                        <div style={{ fontSize: "8px", opacity: 0.7 }}>
                          Offer V{index + 2}
                        </div>
                      </div>
                    }
                    id={`tab-${index + 1}`}
                    aria-controls={`tabpanel-${index + 1}`}
                  />
                ))}
              </Tabs>
            </Box>
            <TabPanel value={tabValue} index={0}>
              <div className="offer-detail1">
                <div className="header-text-re">
                  <div className="head-text">Offer Details</div>
                  <div className="button-group">
                    <Button label="Trust Score" onClick={trustModel} />
                    {/* Show download button only if this is the target tab */}
                    {shouldShowDownloadButton(
                      0,
                      selectedOffer,
                      referenceOffers
                    ) && (
                      <Button
                        label="View Agreement"
                        onClick={() => handleAgreementPopup()}
                      />
                    )}
                  </div>
                </div>
                {
                  <OfferDetails
                    offer={selectedOffer}
                    btnShortlist={false}
                    btnSendQuery={false}
                    btnRequestQuote={false}
                    btnRequestSpecifications={false}
                    btnRequestBestPrice={false}
                    btnNegotiate={false}
                    btnRequestShippingDetails={false}
                    btnLogisticsSupport={false}
                    btnRaiseQuery={true}
                  />
                }
              </div>
            </TabPanel>
            {referenceOffers.map((referenceOffer, index) => (
              <TabPanel key={index} value={tabValue} index={index + 1}>
                <div className="offer-detail1">
                  <div className="header-text-re">
                    <div className="head-text">
                      Reference Offer Details {index + 2}
                    </div>
                    <div className="button-group">
                      <Button label="Trust Score" onClick={trustModel} />
                      {/* Show download button only if this is the target tab */}
                      {shouldShowDownloadButton(
                        index + 1,
                        selectedOffer,
                        referenceOffers
                      ) && (
                        <Button
                          label="View Agreement"
                          onClick={() => handleAgreementPopup(referenceOffer)}
                        />
                      )}
                    </div>
                  </div>
                  {
                    <OfferDetails
                      offer={referenceOffer}
                      btnShortlist={false}
                      btnSendQuery={false}
                      btnRequestQuote={false}
                      btnRequestSpecifications={false}
                      btnRequestBestPrice={false}
                      btnNegotiate={false}
                      btnRequestShippingDetails={false}
                      btnLogisticsSupport={false}
                      btnRaiseQuery={true}
                      breakupTab={true}
                    />
                  }
                </div>
              </TabPanel>
            ))}
          </div>

          {/* RFQ Details Section */}
          {selectedOffer && selectedOffer.RequirementsID && (
            <RFQForm
              formMode="view"
              setFormMode={true}
              selectedRFQ={selectedRFQ}
              setIsFormVisible={true}
              productList={productList}
              allProductMaster={allProductMaster}
              btnCancel={false}
              btnHistory={false}
              btnAddFinanceRfq={false}
            />
          )}
        </>
      )}
      {/* Trust Score Modal */}
      {trustModal && selectedOffer && (
        <TrustScoreModal
          onClose={() => setTrustModal(false)}
          userEmail={selectedOffer.SellerEmailID}
        />
      )}
    </div>
  );
};

export default AcceptedOffers;
